<script setup lang="ts">
import { ref, computed, getCurrentInstance, nextTick } from 'vue';
import AuthLayout from '../components/AuthLayout.vue';
import { Button, Input } from '../components/ui';
import { resendConfirmation } from '../api';
import { signInPath } from '../services/authPaths';
import { isValidEmail } from '../services/validators';

const queryEmail = getCurrentInstance()?.proxy?.$route?.query.email;

const email = ref(typeof queryEmail === 'string' ? queryEmail : '');
const emailError = ref('');
const loading = ref(false);
const success = ref(false);
const successElement = ref<HTMLElement | null>(null);

const topLinks = computed(() => {
  if (success.value) {
    return [{ url: signInPath(email.value), text: 'Back to sign in' }];
  }
  return [
    { url: signInPath(), text: 'Sign in' },
    { url: '/users/password/new', text: 'Forgot password?' },
    { url: '/users/unlock/new', text: "Didn't receive unlock instructions?" },
  ];
});

const bottomLinks = computed(() => {
  if (success.value) return [];
  return [{ url: '/users/request', text: 'Not a member yet? Request access' }];
});

async function handleSubmit() {
  emailError.value = '';

  if (!isValidEmail(email.value)) {
    emailError.value = 'Enter a valid email.';
    return;
  }

  loading.value = true;

  try {
    await resendConfirmation(email.value);
    success.value = true;
    await nextTick();
    successElement.value?.focus();
  } catch (error) {
    emailError.value = error instanceof Error ? error.message : 'Something went wrong. Please try again.';
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <AuthLayout title="Resend confirmation instructions" :top-links="topLinks" :bottom-links="bottomLinks">
    <div
      v-if="success"
      ref="successElement"
      tabindex="-1"
      class="rounded text-left text-white focus:outline-2 focus:outline-offset-4 focus:outline-white"
      role="status"
    >
      <p class="m-0 mb-2 leading-relaxed">
        We're sending confirmation instructions to
        <strong class="break-words text-sw-purple">{{ email }}</strong
        >.
      </p>
      <p class="m-0 text-sm leading-relaxed text-white/75">Not there after a few minutes? Check your spam folder.</p>
    </div>

    <form v-else @submit.prevent="handleSubmit">
      <Input
        id="confirmation-email"
        v-model="email"
        type="email"
        label="Email"
        placeholder="Email"
        autocomplete="email"
        :error="emailError"
      />

      <div class="mt-form-gap">
        <Button type="submit" variant="primary" size="lg" :loading="loading" :disabled="loading">
          Resend confirmation instructions
        </Button>
      </div>
    </form>
  </AuthLayout>
</template>
